import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, Share2, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { API } from "../config/constants";
import { useAuth } from "../context/AuthContext";

const WalletModal = ({ type, onClose, user, refreshUser, t }) => {
  const { authAxios } = useAuth();
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [txHash, setTxHash] = useState("");
  const [tab, setTab] = useState("USDT");
  const [loading, setLoading] = useState(false);
  const [depositWallet, setDepositWallet] = useState("");
  const [withdrawalPin, setWithdrawalPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [bnbPrice, setBnbPrice] = useState(0);
  const MINIMUM_DEPOSIT_USDT = 5;

  // Fetch deposit wallet address and BNB price
  useEffect(() => {
    if (type === "deposit") {
      axios.get(`${API}/deposit-wallet`)
        .then(res => setDepositWallet(res.data.address))
        .catch(() => setDepositWallet(""));
      
      // Get BNB price for minimum calculation
      axios.get(`${API}/crypto/price/BNB`)
        .then(res => setBnbPrice(res.data.price_usd || 0))
        .catch(() => setBnbPrice(0));
    }
  }, [type]);

  // Calculate minimum and maximum BNB amount
  const minBnbAmount = bnbPrice > 0 ? (MINIMUM_DEPOSIT_USDT / bnbPrice).toFixed(4) : "0.00";
  const maxBnbAmount = bnbPrice > 0 ? (50000 / bnbPrice).toFixed(2) : "0.00";

  const handleSubmit = async () => {
    if (!amount && type !== "receive") { toast.error("Enter amount"); return; }
    
    // Withdrawal PIN validation
    if (type === "withdraw") {
      if (!withdrawalPin || withdrawalPin.length !== 6) {
        toast.error("Enter 6-digit withdrawal PIN");
        return;
      }
    }
    
    setLoading(true);
    try {
      // Get token directly from localStorage as backup
      const storedUser = JSON.parse(localStorage.getItem("ltg_user") || '{}');
      const token = storedUser?.access_token;
      
      if (!token) {
        toast.error("Session expired. Please login again.");
        setLoading(false);
        return;
      }
      
      const api = authAxios();
      let endpoint = "", payload = {};
      switch (type) {
        case "deposit": endpoint = `/wallet/deposit/${user.id}`; payload = { currency: tab, amount: parseFloat(amount), tx_hash: txHash }; break;
        case "withdraw": endpoint = `/wallet/withdraw`; payload = { amount: parseFloat(amount), address, withdrawal_pin: withdrawalPin }; break;
        case "buy": endpoint = `/wallet/buy`; payload = { amount: parseFloat(amount) }; break;
        case "sell": endpoint = `/wallet/sell`; payload = { amount: parseFloat(amount) }; break;
        case "send": endpoint = `/wallet/send`; payload = { recipient: address, amount: parseFloat(amount) }; break;
        default: return;
      }
      await api.post(endpoint, payload);
      toast.success("Success!");
      refreshUser();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied!");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Copied!");
      }
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Copied!");
      } catch (e) {
        toast.error("Copy failed - please copy manually");
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content" data-testid={`modal-${type}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-manrope font-bold text-xl capitalize">{t(type)}</h2>
          <button onClick={onClose}><X size={24} /></button>
        </div>

        {type === "deposit" && (
          <>
            <div className="flex gap-2 mb-4">
              {["USDT", "BNB"].map(c => (
                <button key={c} className={`tab-btn ${tab === c ? "active" : ""}`} onClick={() => setTab(c)}>{c}</button>
              ))}
            </div>
            
            {/* Minimum Deposit Warning */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-yellow-500 flex-shrink-0" size={16} />
                {tab === "USDT" ? (
                  <p className="text-sm"><span className="font-bold text-green-400">5 USDT</span> <span className="text-muted-foreground">to</span> <span className="font-bold text-green-400">50,000 USDT</span></p>
                ) : (
                  <p className="text-sm">
                    <span className="font-bold text-green-400">{minBnbAmount} BNB</span> <span className="text-muted-foreground">to</span> <span className="font-bold text-green-400">{maxBnbAmount} BNB</span>
                  </p>
                )}
              </div>
            </div>
            
            <div className="bg-secondary p-4 rounded-xl text-center mb-4">
              <p className="text-muted-foreground text-sm mb-2">Send {tab} to:</p>
              <p className="text-primary text-sm font-mono break-all">{depositWallet || "Loading..."}</p>
              <button onClick={() => copy(depositWallet)} disabled={!depositWallet} className="mt-3 px-4 py-2 bg-card border border-border rounded-full inline-flex items-center gap-2 disabled:opacity-50">
                <Copy size={16} /> {t("copyAddress")}
              </button>
              <p className="text-red-500 text-sm mt-3 font-bold">{t("bscOnly")}</p>
            </div>
            <input type="number" placeholder={`${t("amount")} (${tab})`} value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mb-3" />
            <input type="text" placeholder={t("txHash")} value={txHash} onChange={(e) => setTxHash(e.target.value)} className="input-dark mb-4" />
          </>
        )}

        {type === "withdraw" && (
          <>
            {/* Account Not Activated Warning */}
            {!user.is_activated && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-center">
                <p className="text-red-400 font-semibold">Account Not Activated</p>
                <p className="text-red-300 text-sm mt-1">
                  Deposit ${(user.activation_required || 50).toFixed(0)} to unlock Withdrawal
                </p>
              </div>
            )}
            <p className="text-center text-green-500 mb-2">Balance: ${user.usdt_balance.toFixed(4)}</p>
            <p className="text-center text-yellow-500 text-sm mb-4">{t("minWithdraw")}</p>
            <input type="number" placeholder={`${t("amount")} (min 5 USDT)`} value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mb-3" disabled={!user.is_activated} />
            <input type="text" placeholder="BEP20/BSC Address" value={address} onChange={(e) => setAddress(e.target.value)} className="input-dark mb-3" disabled={!user.is_activated} />
            <div className="relative mb-4">
              <input 
                type={showPin ? "text" : "password"} 
                placeholder="6-digit Withdrawal PIN" 
                value={withdrawalPin} 
                onChange={(e) => setWithdrawalPin(e.target.value.replace(/\D/g, ""))} 
                maxLength={6}
                className="input-dark w-full pr-10"
                disabled={!user.is_activated}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-orange-400 text-xs text-center mb-2">🔐 Enter your 6-digit withdrawal PIN</p>
          </>
        )}

        {type === "buy" && (
          <>
            <p className="text-center text-green-500 mb-2">Available: ${user.usdt_balance.toFixed(4)} USDT</p>
            <p className="text-center text-primary mb-4">{t("rate")}</p>
            <input type="number" placeholder={`${t("amount")} (USDT)`} value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mb-3" />
            {amount && <p className="text-green-500 text-center mb-4">{t("youllGet")}: {(parseFloat(amount) * 1000).toLocaleString()} LTG</p>}
          </>
        )}

        {type === "sell" && (
          <>
            <p className="text-center text-green-500 mb-2">Deposited LTG: {(user.sellable_ltg || 0).toFixed(2)}</p>
            <p className="text-muted-foreground text-xs text-center mb-4">(Only deposited/buy LTG can be sold)</p>
            <input type="number" placeholder={`${t("amount")} (LTG)`} value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mb-3" />
            {amount && <p className="text-primary text-center mb-4">{t("youllGet")}: ${(parseFloat(amount) / 1000).toFixed(4)} USDT</p>}
          </>
        )}

        {type === "send" && (
          <>
            <p className="text-center text-green-500 mb-2">Deposited LTG: {(user.sellable_ltg || 0).toFixed(2)}</p>
            <p className="text-muted-foreground text-xs text-center mb-4">(Only deposited/buy LTG can be send)</p>
            <input type="text" placeholder={t("recipient")} value={address} onChange={(e) => setAddress(e.target.value)} className="input-dark mb-3" />
            <input type="number" placeholder={t("amount")} value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mb-4" />
          </>
        )}

        {type === "receive" && (
          <div className="text-center">
            <p className="text-muted-foreground mb-2">{t("walletAddress")}:</p>
            <p className="text-primary text-lg font-mono break-all mb-4">{user.wallet_address}</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => copy(user.wallet_address)} className="px-6 py-3 bg-card border border-border rounded-full inline-flex items-center gap-2">
                <Copy size={16} /> Copy
              </button>
              <button onClick={async () => {
                try {
                  if (navigator.share) {
                    await navigator.share({ title: "LTG Wallet", text: user.wallet_address });
                  } else {
                    await copy(user.wallet_address);
                  }
                } catch (err) {
                  await copy(user.wallet_address);
                }
              }} className="px-6 py-3 bg-green-600 rounded-full inline-flex items-center gap-2">
                <Share2 size={16} /> {t("share")}
              </button>
            </div>
          </div>
        )}

        {type !== "receive" && (
          <button 
            onClick={handleSubmit} 
            disabled={loading || (type === "withdraw" && !user.is_activated)} 
            className="w-full btn-gold py-3.5 rounded-full text-lg mb-8"
          >
            {type === "withdraw" && !user.is_activated ? "Activate Account First" : loading ? "..." : t("submit")}
          </button>
        )}
      </div>
    </>
  );
};

export default WalletModal;
